import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Fingerprint, Loader2, ShieldAlert } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, formatApiErrorDetail, setCeoReauthHandler } from "../../lib/api";
import { inputCls } from "../forms/FormBits";
import { assertPasskey, passkeySupported } from "../../lib/webauthn";

/* Mounted inside the CEO Control Room. When the server answers 403 RE-AUTHENTICATION REQUIRED
   for a high-risk action, api.js calls the registered handler; this modal runs a fresh step-up
   (passkey preferred, TOTP fallback) and the original request is retried on success. */
export default function CeoReauthModal() {
  const [pending, setPending] = useState(null); // {resolve, reject}
  const [status, setStatus] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setCeoReauthHandler(() => new Promise((resolve, reject) => {
      setPending({ resolve, reject }); setCode("");
      api.get("/ceo/status").then(({ data }) => setStatus(data)).catch(() => setStatus({}));
    }));
    return () => setCeoReauthHandler(null);
  }, []);

  const done = (data) => { sessionStorage.setItem("ceo_csrf", data.csrf); toast.success("CEO re-authentication confirmed"); pending.resolve(); setPending(null); };
  const cancel = () => { pending?.reject(new Error("Re-authentication cancelled")); setPending(null); };

  const viaPasskey = async () => {
    setBusy(true);
    try { done(await assertPasskey("/ceo/reauth/webauthn/options", "/ceo/reauth/webauthn/verify")); }
    catch (e) { toast.error(e.response ? formatApiErrorDetail(e.response.data?.detail) : e.message); }
    finally { setBusy(false); }
  };
  const viaTotp = async (e) => {
    e.preventDefault(); setBusy(true);
    try { done((await api.post("/ceo/reauth/totp", { code })).data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const passkeyOk = passkeySupported() && status?.methods?.webauthn === "available";
  return (
    <Dialog open={!!pending} onOpenChange={(o) => { if (!o) cancel(); }}>
      <DialogContent className="max-w-md" data-testid="ceo-reauth-modal">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-serif text-navy"><ShieldAlert className="h-5 w-5 text-gold" /> Re-authentication required</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">This is a high-risk CEO action. Confirm your identity again with a fresh step-up.</p>
        {passkeyOk && (
          <button onClick={viaPasskey} disabled={busy} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gold px-5 py-3 text-sm font-bold text-white hover:bg-gold-dark disabled:opacity-60" data-testid="ceo-reauth-passkey">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Fingerprint className="h-4 w-4" />} Verify with passkey
          </button>
        )}
        {status?.mfa_enrolled && (
          <form onSubmit={viaTotp} className="space-y-2" data-testid="ceo-reauth-totp-form">
            <p className="text-xs text-muted-foreground">{passkeyOk ? "Or use your authenticator code (fallback)" : "Authenticator code"}</p>
            <input inputMode="numeric" className={inputCls} value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" data-testid="ceo-reauth-code" />
            <button disabled={busy || code.length < 6} className="w-full rounded-lg bg-navy px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60" data-testid="ceo-reauth-submit">Verify</button>
          </form>
        )}
        <button onClick={cancel} className="text-xs text-muted-foreground hover:text-navy" data-testid="ceo-reauth-cancel">Cancel</button>
      </DialogContent>
    </Dialog>
  );
}
